import React, { useState, useEffect, createContext, useContext } from 'react';
import type { Notification } from '../types/notifications';
import { sendSmoodFundingNotification } from '../services/lineNotificationService';

// Maximum notifications to keep per user
const MAX_NOTIFICATIONS_PER_USER = 50;

export interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'isRead'>) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: (userId: string) => void;
  getUnreadCount: (userId: string) => number;
  clearNotifications: (userId: string) => void;
}

export const NotificationContext = createContext<NotificationContextType | null>(null);

/**
 * Helper function to enforce notification limit per user
 * Keeps only the most recent MAX_NOTIFICATIONS_PER_USER notifications for each user
 */
const enforceNotificationLimit = (notifications: Notification[]): Notification[] => {
  // Group notifications by userId
  const notificationsByUser: Record<string, Notification[]> = {};
  
  notifications.forEach(notif => {
    if (!notificationsByUser[notif.userId]) {
      notificationsByUser[notif.userId] = [];
    }
    notificationsByUser[notif.userId].push(notif);
  });
  
  // For each user, keep only the most recent MAX_NOTIFICATIONS_PER_USER
  const limitedNotifications: Notification[] = [];
  
  Object.values(notificationsByUser).forEach(userNotifications => {
    // Sort by createdAt descending (newest first)
    const sorted = userNotifications.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    // Keep only the first MAX_NOTIFICATIONS_PER_USER
    limitedNotifications.push(...sorted.slice(0, MAX_NOTIFICATIONS_PER_USER));
  });
  
  // Sort all notifications by createdAt descending
  return limitedNotifications.sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const savedNotifications = localStorage.getItem('expense-tracker-notifications');
    if (savedNotifications) {
      try {
        const decryptedNotifications = JSON.parse(savedNotifications);
        // Apply limit when loading from localStorage (cleanup old data)
        const limitedNotifications = enforceNotificationLimit(decryptedNotifications || []);
        setNotifications(limitedNotifications);
      } catch (error) {
        setNotifications([]); // Fallback to empty array on error
      }
    }
  }, []);

  useEffect(() => {
    if (notifications.length > 0) {
      localStorage.setItem('expense-tracker-notifications', JSON.stringify(notifications));
    } else {
      // Optionally, remove the item if notifications array becomes empty
      localStorage.removeItem('expense-tracker-notifications');
    }
  }, [notifications]);

  const addNotification = (notification: Omit<Notification, 'id' | 'createdAt' | 'isRead'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString(),
      isRead: false,
    };
    
    setNotifications(prevNotifications => {
      // Add new notification at the beginning
      const updatedNotifications = [newNotification, ...prevNotifications];
      // Enforce the limit per user
      return enforceNotificationLimit(updatedNotifications);
    });

    // Also send LINE notification asynchronously (fire and forget)
    // This won't block the UI and will fail silently if user doesn't have LINE linked
    sendSmoodFundingNotification(
      notification.userId,
      notification.type,
      notification.title,
      notification.message,
      notification.projectName,
      notification.itemName
    ).catch(() => {
      // Silently fail - LINE notification is optional
    });
  };

  const markAsRead = (notificationId: string) => {
    setNotifications(prevNotifications =>
      prevNotifications.map(notif =>
        notif.id === notificationId ? { ...notif, isRead: true } : notif
      )
    );
  };

  const markAllAsRead = (userId: string) => {
    setNotifications(prevNotifications =>
      prevNotifications.map(notif =>
        notif.userId === userId ? { ...notif, isRead: true } : notif
      )
    );
  };

  const getUnreadCount = (userId: string): number => {
    return notifications.filter(notif => notif.userId === userId && !notif.isRead).length;
  };

  const clearNotifications = (userId: string) => {
    setNotifications(prevNotifications => prevNotifications.filter(notif => notif.userId !== userId));
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        markAsRead,
        markAllAsRead,
        getUnreadCount,
        clearNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
